import type {
  BrokenWingButterfly,
  IronFly,
  Mode,
  Regime,
  Structure,
} from "@/types/options";

// ── Continuous parameter interface ───────────────────────────────────────────

export interface StructureParams {
  ifWidth: number; // Iron fly wing width (20–120)
  u1: number; // Upper BWB: first short offset (20–80)
  u2: number; // Upper BWB: second short offset (50–160)
  u3: number; // Upper BWB: long wing offset (80–200)
  d1: number; // Lower BWB: first short offset (20–80)
  d2: number; // Lower BWB: second short offset (50–160)
  d3: number; // Lower BWB: long wing offset (80–200)
  skew: number; // Skew bias -1 (bearish) to +1 (bullish), 0 = neutral
}

// ── Preset snapshots ─────────────────────────────────────────────────────────

const IF_WIDTHS: Record<Regime, number> = {
  range: 35,
  neutral: 50,
  trend: 70,
  breakout: 90,
};

interface WingSpacing {
  u1: number;
  u2: number;
  u3: number;
  d1: number;
  d2: number;
  d3: number;
}

const WING_SPACINGS: Record<Mode, WingSpacing> = {
  conservative: { u1: 35, u2: 85, u3: 120, d1: 35, d2: 85, d3: 120 },
  neutral: { u1: 45, u2: 100, u3: 135, d1: 65, d2: 125, d3: 170 },
  aggressive: { u1: 55, u2: 115, u3: 150, d1: 75, d2: 145, d3: 190 },
};

export function getPresetParams(regime: Regime, mode: Mode): StructureParams {
  const wings = WING_SPACINGS[mode];
  return {
    ifWidth: IF_WIDTHS[regime],
    ...wings,
    skew: 0,
  };
}

// ── Core generator ───────────────────────────────────────────────────────────

export function roundToStrike(price: number, increment: number = 5): number {
  return Math.round(price / increment) * increment;
}

export function generateStructureFromParams(
  spot: number,
  params: StructureParams,
): Structure {
  const center = roundToStrike(spot);
  const { ifWidth, u1, u2, u3, d1, d2, d3, skew } = params;

  // Apply skew: positive skew shifts the iron fly center up, negative down
  const skewOffset = roundToStrike(skew * 25);

  const ironFly: IronFly = {
    shortCall: center + skewOffset,
    shortPut: center + skewOffset,
    longCall: center + skewOffset + ifWidth,
    longPut: center + skewOffset - ifWidth,
  };

  const upperBWB: BrokenWingButterfly = {
    long1: center,
    short1: center + u1,
    short2: center + u2,
    long2: center + u3,
  };

  const lowerBWB: BrokenWingButterfly = {
    long1: center,
    short1: center - d1,
    short2: center - d2,
    long2: center - d3,
  };

  return { center, ironFly, upperBWB, lowerBWB };
}
